import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Auth token key - single source of truth
export const AUTH_TOKEN_KEY = 'auth_token';

// Helper to get auth token
export const getAuthToken = () => sessionStorage.getItem(AUTH_TOKEN_KEY);

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests (use sessionStorage for better security)
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Skip redirect for auth endpoints - let the component handle the error
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') || 
                           error.config?.url?.includes('/auth/register');
    
    if (error.response?.status === 401 && !isAuthEndpoint) {
      // Only redirect for expired sessions on protected endpoints, not login failures
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  updateProfile: (data) => api.patch('/auth/profile', data),
};

// Intake
export const intakeAPI = {
  create: (data) => api.post('/intake', data),
  getMyIntakes: () => api.get('/intake/my'),
  getQueue: () => api.get('/intake/queue'),
  getById: (id) => api.get(`/intake/${id}`),
  getByPatient: (patientId) => api.get(`/intake/patient/${patientId}`),
  updateTriage: (id, data) => api.patch(`/intake/${id}/triage`, data),
};

// Check-In / Check-Out
export const checkinAPI = {
  // Check-in
  checkIn: (data) => api.post('/checkin', data),
  getWaiting: () => api.get('/checkin/waiting'),
  getMyCheckin: () => api.get('/checkin/my'),
  updateStatus: (checkinId, status) => api.patch(`/checkin/${checkinId}/status?status=${status}`),
  getHistory: (patientId, days = 7) => api.get(`/checkin/history?${patientId ? `patient_id=${patientId}&` : ''}days=${days}`),
  
  // Identity verification
  verifyIdentity: (data) => api.post('/checkin/verify-identity', data),
  getIdentityStatus: () => api.get('/checkin/verify-identity/status'),
  
  // Check-out
  checkout: (data) => api.post('/checkout', data),
  getCheckout: (checkinId) => api.get(`/checkout/${checkinId}`),
};

// AI
export const aiAPI = {
  triage: (data) => api.post('/ai/triage', data),
  scribe: (data) => api.post('/ai/scribe', data),
  chat: (data) => api.post('/ai/chat', data),
  careGaps: () => api.post('/ai/care-gaps'),
  transcribe: (data) => api.post('/ai/transcribe', data),
};

// Encounters
export const encounterAPI = {
  create: (intakeId) => api.post(`/encounters?intake_id=${intakeId}`),
  getAll: () => api.get('/encounters'),
  getById: (id) => api.get(`/encounters/${id}`),
  update: (id, data) => api.patch(`/encounters/${id}`, data),
  // Patient Search & Encounter History
  searchPatients: (lastName, firstName, dob) => {
    const params = new URLSearchParams();
    if (lastName) params.append('last_name', lastName);
    if (firstName) params.append('first_name', firstName);
    if (dob) params.append('dob', dob);
    return api.get(`/encounters/search/patients?${params.toString()}`);
  },
  getPatientEncounters: (patientId, status, encounterType) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (encounterType) params.append('encounter_type', encounterType);
    return api.get(`/encounters/patient/${patientId}?${params.toString()}`);
  },
  // Addendums
  addAddendum: (encounterId, text, reason) => api.post(`/encounters/${encounterId}/addendum`, { text, reason }),
  getAddendums: (encounterId) => api.get(`/encounters/${encounterId}/addendums`),
};

// Patient Chart (Filing System)
export const patientChartAPI = {
  getChart: (patientId) => api.get(`/patients/${patientId}/chart`),
  getPatientsList: () => api.get('/patients/list'),
  exportChartPdf: async (patientId) => {
    const response = await api.get(`/patient-chart/${patientId}/export`, { responseType: 'blob' });
    return response;
  },
};

// Appointments
export const appointmentAPI = {
  create: (data) => api.post('/appointments', data),
  getAll: () => api.get('/appointments'),
  updateStatus: (id, status) => api.patch(`/appointments/${id}/status?status=${status}`),
  checkAvailability: (date) => api.get(`/appointments/check-availability?date=${date}`),
  // Reschedule/Cancel requests
  requestReschedule: (id, data) => api.post(`/appointments/${id}/request-reschedule`, data),
  requestCancel: (id, data) => api.post(`/appointments/${id}/request-cancel`, data),
  approveReschedule: (id) => api.post(`/appointments/${id}/approve-reschedule`),
  denyReschedule: (id) => api.post(`/appointments/${id}/deny-reschedule`),
  approveCancel: (id) => api.post(`/appointments/${id}/approve-cancel`),
  denyCancel: (id) => api.post(`/appointments/${id}/deny-cancel`),
  getPendingRequests: () => api.get('/appointments/pending-requests'),
};

// Messages
export const messageAPI = {
  send: (data) => api.post('/messages', data),
  getAll: () => api.get('/messages'),
  markRead: (id) => api.patch(`/messages/${id}/read`),
};

// Staff (for messaging between CMA, Provider, Admin)
export const staffAPI = {
  getAll: () => api.get('/staff'),
};

// Video Calls
export const videoAPI = {
  // Rooms
  createRoom: (data) => api.post('/video/rooms', data),
  getRooms: () => api.get('/video/rooms'),
  getRoom: (roomId) => api.get(`/video/rooms/${roomId}`),
  joinRoom: (roomId) => api.post(`/video/rooms/${roomId}/join`),
  leaveRoom: (roomId) => api.post(`/video/rooms/${roomId}/leave`),
  endRoom: (roomId) => api.post(`/video/rooms/${roomId}/end`),
  getParticipants: (roomId) => api.get(`/video/rooms/${roomId}/participants`),
  getHistory: () => api.get('/video/history'),
  
  // Waiting Room
  getWaitingRoom: (roomId) => api.get(`/video/rooms/${roomId}/waiting`),
  admitUser: (roomId, userId) => api.post(`/video/rooms/${roomId}/admit/${userId}`),
  denyUser: (roomId, userId) => api.post(`/video/rooms/${roomId}/deny/${userId}`),
  
  // Chat
  sendChat: (roomId, content) => api.post(`/video/rooms/${roomId}/chat?content=${encodeURIComponent(content)}`),
  getChat: (roomId) => api.get(`/video/rooms/${roomId}/chat`),
  
  // Recording
  startRecording: (roomId) => api.post(`/video/rooms/${roomId}/recording/start`),
  stopRecording: (roomId) => api.post(`/video/rooms/${roomId}/recording/stop`),
};

// Visit Summaries
export const visitSummaryAPI = {
  send: (patientId, encounterId = null, followUpDate = null, followUpInstructions = null) => {
    let url = `/visit-summary/send?patient_id=${patientId}`;
    if (encounterId) url += `&encounter_id=${encounterId}`;
    if (followUpDate) url += `&followup_date=${followUpDate}`;
    if (followUpInstructions) url += `&followup_instructions=${encodeURIComponent(followUpInstructions)}`;
    return api.post(url);
  },
  getPatientSummaries: (patientId) => api.get(`/visit-summary/patient/${patientId}`),
  getById: (summaryId) => api.get(`/visit-summary/${summaryId}`),
  downloadPdf: async (summaryId) => {
    const response = await api.get(`/visit-summary/${summaryId}/pdf`, { responseType: 'blob' });
    return response;
  },
};

// Admin
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
  getStats: () => api.get('/admin/stats'),
  getProviders: () => api.get('/providers'),
  getAnalytics: () => api.get('/admin/analytics'),
};

// Insurance
export const insuranceAPI = {
  add: (data) => api.post('/insurance', data),
  getMy: () => api.get('/insurance/my'),
  getPatient: (patientId) => api.get(`/insurance/patient/${patientId}`),
  verify: (id, data) => api.patch(`/insurance/${id}/verify`, data),
  getPendingVerifications: () => api.get('/insurance/pending-verification'),
};

// Billing
export const billingAPI = {
  createInvoice: (patientId, data) => api.post(`/billing/invoice?patient_id=${patientId}`, data),
  getInvoices: () => api.get('/billing/invoices'),
  getInvoice: (id) => api.get(`/billing/invoice/${id}`),
  recordPayment: (data) => api.post('/billing/payment', data),
  getPayments: (invoiceId) => api.get(`/billing/payments/${invoiceId}`),
  getSummary: () => api.get('/billing/summary'),
  getDailyReport: (date) => api.get(`/billing/reports/daily${date ? `?date=${date}` : ''}`),
  // Billing codes
  getICD10Codes: (search) => api.get(`/billing/codes/icd10${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  getCPTCodes: (search) => api.get(`/billing/codes/cpt${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  getHCPCSCodes: (search) => api.get(`/billing/codes/hcpcs${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  aiSuggestCodes: (data) => api.post('/billing/codes/ai-suggest', data),
  // Stripe payments
  createCheckout: (invoiceId) => api.post('/payments/create-checkout', { 
    invoice_id: invoiceId, 
    origin_url: window.location.origin 
  }),
  getPaymentStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getTransactions: () => api.get('/payments/transactions'),
};

// Prescriptions
export const prescriptionAPI = {
  // Legacy endpoints (billing.py)
  create: (data) => api.post('/prescriptions', data),
  getAll: () => api.get('/prescriptions'),
  getPatient: (patientId) => api.get(`/prescriptions/patient/${patientId}`),
  updateStatus: (id, status) => api.patch(`/prescriptions/${id}/status?status=${status}`),
  sendToPharmacy: (id, pharmacyData) => api.post(`/prescriptions/${id}/send-to-pharmacy`, pharmacyData),
  getPharmacyHistory: (id) => api.get(`/prescriptions/${id}/pharmacy-history`),
  
  // Patient refill endpoints (prescriptions.py)
  getMyMedications: () => api.get('/prescriptions/my-medications'),
  requestRefill: (data) => api.post('/prescriptions/refill-request', data),
  getMyRefillRequests: () => api.get('/prescriptions/my-refill-requests'),
  
  // CMA/Provider prescription management (prescriptions.py - new /rx endpoints)
  getPatientPrescriptions: (patientId, includeInactive = false) => 
    api.get(`/rx/patient/${patientId}?include_inactive=${includeInactive}`),
  createRx: (data) => api.post('/rx', data),
  updateRx: (id, data) => api.patch(`/rx/${id}`, data),
  discontinueRx: (id) => api.delete(`/rx/${id}`),
  
  // Refill request management (prescriptions.py)
  getPendingRefills: () => api.get('/prescriptions/refill-requests/pending'),
  getAllRefills: (status, limit = 100) => 
    api.get(`/prescriptions/refill-requests/all?${status ? `status=${status}&` : ''}limit=${limit}`),
  reviewRefill: (id, data) => api.post(`/prescriptions/refill-requests/${id}/review`, data),
  completeRefill: (id) => api.post(`/prescriptions/refill-requests/${id}/complete`),
};

// Care Gaps
export const careGapAPI = {
  create: (patientId, gapType, description, priority) => 
    api.post(`/care-gaps?patient_id=${patientId}&gap_type=${gapType}&description=${description}&priority=${priority}`),
  getAll: () => api.get('/care-gaps'),
  updateStatus: (id, status, notes) => api.patch(`/care-gaps/${id}/status?status=${status}${notes ? `&notes=${notes}` : ''}`),
  aiDetect: (patientId) => api.post(`/care-gaps/ai-detect?patient_id=${patientId}`),
};

// HIPAA Consents
export const hipaaAPI = {
  getTemplates: () => api.get('/hipaa/templates'),
  getTemplate: (type) => api.get(`/hipaa/template/${type}`),
  signConsent: (data) => api.post('/hipaa/consent', data),
  getMyConsents: () => api.get('/hipaa/consents'),
  getPatientConsents: (patientId) => api.get(`/hipaa/consents/patient/${patientId}`),
  getStatus: () => api.get('/hipaa/status'),
  getPatientStatus: (patientId) => api.get(`/hipaa/status/patient/${patientId}`),
  revokeConsent: (consentId, reason) => api.post(`/hipaa/revoke/${consentId}${reason ? `?reason=${reason}` : ''}`),
  getConsentDetail: (consentId) => api.get(`/hipaa/consent/${consentId}`),
  // Admin: Get all consents
  getAllConsents: () => api.get('/hipaa/consents/all'),
};

// Notifications
export const notificationAPI = {
  getAll: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/mark-all-read'),
};

// Documents
export const documentAPI = {
  upload: (data) => api.post('/documents', data),
  getAll: (includeInactive = false) => api.get(`/documents?include_inactive=${includeInactive}`),
  getMyDocs: (includeInactive = false) => api.get(`/documents?include_inactive=${includeInactive}`),
  getPatientDocs: (patientId, includeInactive = false) => api.get(`/documents/patient/${patientId}?include_inactive=${includeInactive}`),
  getHistory: (patientId, documentType = null) => api.get(`/documents/history/${patientId}${documentType ? `?document_type=${documentType}` : ''}`),
  getById: (id) => api.get(`/documents/${id}`),
  delete: (id) => api.delete(`/documents/${id}`),
  getTypes: () => api.get('/documents/types'),
  getExpiring: () => api.get('/documents/expiring'),
  updateExpiration: (id, expirationDate) => api.patch(`/documents/${id}/expiration`, { expiration_date: expirationDate }),
  extractExpiry: (imageBase64, documentType) => api.post('/documents/extract-expiry', { image_base64: imageBase64, document_type: documentType }),
  // Admin: Get all documents
  getAllDocuments: () => api.get('/documents/all'),
  // HIPAA consent check for check-in
  getHipaaConsent: () => api.get('/hipaa/status'),
  getHipaaTemplates: () => api.get('/hipaa/templates'),
  signHipaaConsent: (data) => api.post('/hipaa/consent', data),
  // Export features
  exportSingle: async (id) => {
    const response = await api.get(`/documents/${id}/export`, { responseType: 'blob' });
    return response;
  },
  exportBulk: async (documentIds) => {
    const response = await api.post('/documents/export/bulk', { document_ids: documentIds }, { responseType: 'blob' });
    return response;
  },
  emailDocuments: (documentIds, recipientEmail, recipientName, message) => 
    api.post('/documents/email', { 
      document_ids: documentIds, 
      recipient_email: recipientEmail,
      recipient_name: recipientName || null,
      message: message || null
    }),
};

// Medications
export const medicationAPI = {
  search: (query) => api.get(`/medications/search?query=${query}`),
  getPatientMeds: (patientId, activeOnly = true) => api.get(`/medications/patient/${patientId}?active_only=${activeOnly}`),
  add: (data) => api.post('/medications', data),
  update: (id, data) => api.patch(`/medications/${id}`, data),
  delete: (id) => api.delete(`/medications/${id}`),
  import: (data) => api.post('/medications/import', data),
  checkInteractions: (medications) => api.post('/medications/check-interactions', { medication_names: medications }),
  aiReview: (patientId) => api.post(`/medications/ai-review?patient_id=${patientId}`),
  verify: (id) => api.patch(`/medications/${id}/verify`),
  extractFromImage: (data) => api.post('/medications/extract-from-image', data),
  getUnverified: () => api.get('/medications/unverified'),
};

// Lab Results
export const labAPI = {
  extract: (documentId) => api.post('/labs/extract', { document_id: documentId }),
  create: (data) => api.post('/labs', data),
  getPatientLabs: (patientId) => api.get(`/labs/patient/${patientId}`),
  getById: (id) => api.get(`/labs/${id}`),
  review: (id, notes) => api.patch(`/labs/${id}/review${notes ? `?notes=${encodeURIComponent(notes)}` : ''}`),
  aiAnalyze: (patientId) => api.post(`/labs/ai-analyze?patient_id=${patientId}`),
  // Admin: Get all labs
  getAllLabs: () => api.get('/labs/all'),
  // Lab Orders
  getTestCatalog: () => api.get('/labs/test-catalog'),
  createOrder: (data) => api.post('/labs/orders', data),
  getOrders: (patientId, status) => api.get(`/labs/orders${patientId || status ? '?' : ''}${patientId ? `patient_id=${patientId}` : ''}${patientId && status ? '&' : ''}${status ? `status=${status}` : ''}`),
  getOrder: (orderId) => api.get(`/labs/orders/${orderId}`),
  updateOrder: (orderId, data) => api.patch(`/labs/orders/${orderId}`, data),
  sendOrder: (orderId) => api.post(`/labs/orders/${orderId}/send`),
  cancelOrder: (orderId) => api.delete(`/labs/orders/${orderId}`),
};

// Pilot Program API
export const pilotAPI = {
  // Clinics
  enrollClinic: (data) => api.post('/pilot/clinics', data),
  getClinics: (status) => api.get(`/pilot/clinics${status ? `?status=${status}` : ''}`),
  getClinic: (clinicId) => api.get(`/pilot/clinics/${clinicId}`),
  updateClinic: (clinicId, data) => api.patch(`/pilot/clinics/${clinicId}`, data),
  startPilot: (clinicId) => api.post(`/pilot/clinics/${clinicId}/start`),
  cancelPilot: (clinicId) => api.delete(`/pilot/clinics/${clinicId}`),
  // Metrics
  recordMetrics: (data) => api.post('/pilot/metrics', data),
  getMetrics: (clinicId) => api.get(`/pilot/metrics/${clinicId}`),
  // Reports
  getROIReport: (clinicId, reportType = 'full') => api.get(`/pilot/reports/${clinicId}/roi?report_type=${reportType}`),
  // Dashboard
  getDashboard: () => api.get('/pilot/dashboard'),
};

// Patients (for CMA/Provider access)
export const patientAPI = {
  getAll: (search) => api.get(`/patients${search ? `?search=${encodeURIComponent(search)}` : ''}`),
};

// Reminders API
export const reminderAPI = {
  getSettings: () => api.get('/reminders/settings'),
  updateSettings: (data) => api.put('/reminders/settings', data),
  sendReminder: (data) => api.post('/reminders/send', data),
  getPending: () => api.get('/reminders/pending'),
  sendBatch: () => api.post('/reminders/send-batch'),
  getLogs: (params) => api.get(`/reminders/logs${params ? `?${new URLSearchParams(params)}` : ''}`),
  getConfigStatus: () => api.get('/reminders/config-status'),
};

// Reports API
export const reportAPI = {
  getAvailable: () => api.get('/reports/available'),
  downloadConsentCompliance: (format = 'pdf') => 
    api.get(`/reports/consent-compliance?format=${format}`, { responseType: 'blob' }),
  downloadBillingSummary: (format = 'pdf', startDate, endDate) => 
    api.get(`/reports/billing-summary?format=${format}${startDate ? `&start_date=${startDate}` : ''}${endDate ? `&end_date=${endDate}` : ''}`, { responseType: 'blob' }),
  downloadAppointments: (format = 'pdf', startDate, endDate) => 
    api.get(`/reports/appointments?format=${format}${startDate ? `&start_date=${startDate}` : ''}${endDate ? `&end_date=${endDate}` : ''}`, { responseType: 'blob' }),
  downloadAdminSummary: (format = 'pdf') => 
    api.get(`/reports/admin-summary?format=${format}`, { responseType: 'blob' }),
};

// Scheduled Reports API
export const scheduledReportAPI = {
  getAll: () => api.get('/scheduled-reports'),
  create: (data) => api.post('/scheduled-reports', data),
  get: (id) => api.get(`/scheduled-reports/${id}`),
  update: (id, data) => api.patch(`/scheduled-reports/${id}`, data),
  delete: (id) => api.delete(`/scheduled-reports/${id}`),
  sendNow: (id) => api.post(`/scheduled-reports/${id}/send-now`),
  getHistory: (limit = 50) => api.get(`/scheduled-reports/history/all?limit=${limit}`),
};

// AI CMA (Virtual Medical Assistant) API
export const aiCmaAPI = {
  getConfig: () => api.get('/ai-cma/config'),
  startSession: (appointmentId) => api.post('/ai-cma/start-session', null, { 
    params: appointmentId ? { appointment_id: appointmentId } : {} 
  }),
  chat: (data) => api.post('/ai-cma/chat', data),
  voiceInput: (data) => api.post('/ai-cma/voice-input', data),
  textToSpeech: (text) => api.post('/ai-cma/text-to-speech', null, { 
    params: { text },
    responseType: 'blob'
  }),
  completeSession: (sessionId) => api.post('/ai-cma/complete-session', null, {
    params: sessionId ? { session_id: sessionId } : {}
  }),
  getSession: (appointmentId) => api.get('/ai-cma/session', {
    params: appointmentId ? { appointment_id: appointmentId } : {}
  }),
  // Staff endpoints
  getPatientSessions: (patientId) => api.get(`/ai-cma/sessions/${patientId}`),
  getReviews: (status = 'pending_review') => api.get('/ai-cma/reviews', { params: { status } }),
  approveReview: (sessionId, data) => api.post(`/ai-cma/reviews/${sessionId}/approve`, data),
  rejectReview: (sessionId, data) => api.post(`/ai-cma/reviews/${sessionId}/reject`, data),
};

// Home Labs API (SiPhox & Rythm Integration)
export const homeLabsAPI = {
  // Panels
  getPanels: (provider) => api.get('/home-labs/panels', { params: provider ? { provider } : {} }),
  
  // Orders
  getOrders: (params) => api.get('/home-labs/orders', { params }),
  getOrder: (orderId) => api.get(`/home-labs/orders/${orderId}`),
  createOrder: (data) => api.post('/home-labs/orders', data),
  updateStatus: (orderId, status, note) => api.patch(`/home-labs/orders/${orderId}/status`, null, { 
    params: { status, note } 
  }),
  
  // Results
  uploadResults: (orderId, data) => api.post(`/home-labs/orders/${orderId}/results`, data),
  getPatientResults: (patientId) => api.get(`/home-labs/patient/${patientId}/results`),
  getBiomarkerTrends: (patientId, biomarker) => api.get(`/home-labs/patient/${patientId}/trends`, {
    params: { biomarker }
  }),
  
  // SiPhox Integration
  sendToSiphox: (orderId) => api.post(`/home-labs/siphox/send-order/${orderId}`),
  
  // Config
  getConfig: () => api.get('/home-labs/config'),
};

// Health Devices API (Wearables - Terra, Dexcom, Sahha)
export const healthDevicesAPI = {
  // Providers & Config
  getProviders: () => api.get('/health-devices/providers'),
  getDataTypes: () => api.get('/health-devices/data-types'),
  getConfig: () => api.get('/health-devices/config'),
  
  // Connections
  getConnections: (patientId) => api.get('/health-devices/connections', { params: patientId ? { patient_id: patientId } : {} }),
  connectTerra: (provider) => api.post(`/health-devices/connect/terra?provider=${provider}`),
  connectDexcom: () => api.post('/health-devices/connect/dexcom'),
  dexcomCallback: (code, state) => api.post('/health-devices/callback/dexcom', null, { params: { code, state } }),
  syncDexcom: (patientId) => api.post('/health-devices/dexcom/sync', null, { params: patientId ? { patient_id: patientId } : {} }),
  connectSahha: () => api.post('/health-devices/connect/sahha'),
  disconnect: (provider) => api.post('/health-devices/disconnect', null, { params: { provider } }),
  
  // Data
  getData: (params) => api.get('/health-devices/data', { params }),
  getSummary: (patientId) => api.get(`/health-devices/summary/${patientId}`),
  getGlucoseChart: (patientId, days = 7) => api.get(`/health-devices/glucose-chart/${patientId}`, { params: { days } }),
  
  // Manual Entry
  addManualEntry: (data) => api.post('/health-devices/manual-entry', data),

  // Alerts (staff)
  getAlerts: (status = 'open') => api.get('/health-devices/alerts', { params: { status } }),
  acknowledgeAlert: (alertId) => api.post(`/health-devices/alerts/${alertId}/acknowledge`),
};

export default api;
